<template>
  <div class="p-4">
    <h2 class="pb-4 font-medium text-gray-800">{{ $t('pages.users') }}</h2>
    <BaseTable
      v-if="users"
      :headers="tableHeaders"
      :list="tableUsers"
      table-id="users"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @tableItemClicked="onEdit"
      :clickable="true"
      @refresh-data="refreshData"
      hide-grouping
      is-saving-layout-hidden
      is-download-button-hidden
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            @click="onDeleteRow(item as User)"
            type="button"
            class="rounded-full p-1.5 text-red-400 hover:bg-red-50">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import { useToastStore } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { type User } from '@/types/Users';
import { getAllUsers, removeUsersByIds } from '@/services/api/user';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import router from '@/router';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { Users } from '@/utils/constants';
const { t: $t } = useI18n({ useScope: 'global' });
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

const users = ref<User[]>([]);
const filterIsActive = ref<boolean | undefined>();
const toastStore = useToastStore();
const { username } = storeToRefs(useAuthStore());
const tableHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'userName', text: $t('auth.username'), type: 'text', active: true },
  { value: 'companySiteList', text: $t('settings.companySiteList'), type: 'text', active: true },
  { value: 'roleList', text: $t('settings.roles'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

async function loadData() {
  users.value = await getAllUsers();
}

const tableUsers = computed(() => {
  if (username.value === Users.Admin) return users.value;
  return users.value.filter((x) => x.userName != Users.Admin);
});

const modalDeleteRow = ref(false);
const deleteRowItem = ref<User>();
function onDeleteRow(item: User) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeUsersByIds([idToDelete]);
  if (!res && users.value) users.value = users.value.filter((user) => user.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteMultiselect(items: User[]) {
  const itemsArray = Array.isArray(items) ? items : [items];
  const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.error({ text: 'Selected items cannot be deleted.' });
    return;
  }

  const idsToDelete = itemsToDelete.map((item) => item.id);
  const res = await removeUsersByIds(idsToDelete);
  if (!res && users.value)
    users.value = users.value.filter((user) => !idsToDelete.includes(user.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function refreshData(data: TableFilterData) {
  await loadData();

  if (data.query) {
    users.value = filterData(users.value, data.query, [
      'name',
      'userName',
      'roleNames',
      'companySiteNames',
    ]);
  }

  if (data.column !== undefined && data.order) {
    users.value = sortData(users.value, data.column, data.order, tableHeaders);
  }

  if (data.isActive !== undefined) users.value = filterActive(users.value, data.isActive);
}
async function onEdit(item: User) {
  router.push({ name: 'UserDetails', params: { id: item.id } });
}
function onAddNew() {
  router.push({ name: 'UserDetails', params: { id: uuidv4() } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
